import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  FormControl,
  InputLabel,
  ListSubheader,
  Paper,
  TextField,
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Box } from '@mui/system'
import { useDispatch } from 'react-redux'
import { debounce, isNotEmpty } from '../../utils/helpers'
import UseTranslation from '../../hooks/UseTranslation'
import { ReactComponent as TeamSnap } from '../../images/TeamSnap.svg'
import LoadMoreBtn from '../PageHelpers/LoadMoreBtn'
import useIosDropdownPlacement, { isIosDevice } from '../../hooks/useIosDropdownPlacement'

const OfoqMultiAutoCompleteWithLoadMore = ({
  name,
  store,
  defaultOption,
  refState,
  nameConverter,
  required,
  customStyle,
  loadMoreActionToDispatch,
  searchActionToDispatch,
  actionQueries = {},
  multiple = true,
  customPlaceHolder,
  customSearchPlaceholder,
  reduxStore,

  ...props
}) => {
  const splitName = name?.split('_')?.join(' ');
  const [options, setOptions] = useState(store || []);
  const [search, setSearch] = useState('');
  const { t } = UseTranslation();

  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()
  const anchorRef = useRef(null)
  // A search field sits above the list, so measure the listbox itself.
  const iosPlacement = useIosDropdownPlacement(anchorRef, open)
  // `null` for single, `[]` for multiple; the wrong one eats typed text on blur.
  const emptyValue = multiple ? [] : null

  useEffect(() => {
    setOptions(store || []);
  }, [store]);

  const sendSelectedItem = (value) => {
    refState(value);
  };

  const handleInputChange = useCallback(
    debounce((newValue) => {
      dispatch(searchActionToDispatch({ queries: newValue, actionQueries }))
    }, 600),
    [searchActionToDispatch],
  );

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = (_, reason) => {
    if (reason === 'toggleInput') {
      setOpen(false)
    }
  }

  const onClickAway = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    setOpen(false)
  }

  // Values the popup needs, refreshed on every render. The component below reads
  // them from here so its own identity can stay constant.
  //
  // Why: an inline `PaperComponent={({children}) => …}` is a NEW component type
  // on each render, so React unmounted and remounted the whole popup whenever
  // `search` changed - destroying the focused search box mid-typing. autoFocus
  // re-focused it, but in the gap a keystroke could land on another field on the
  // form. A stable type re-renders instead, so the input is never destroyed.
  const popupCtx = useRef({})
  popupCtx.current = {
    onClickAway,
    iosPlacement,
    search,
    setSearch,
    handleInputChange,
    customSearchPlaceholder,
    splitName,
    t,
  }

  const PopupPaper = useCallback(({ children }) => {
    const ctx = popupCtx.current

    return (
      <ClickAwayListener onClickAway={ctx.onClickAway}>
        <Paper
          key="Paper2"
          anchorEl={anchorRef?.current}
          sx={[
            ctx.iosPlacement.maxHeight ? {
              maxHeight: `${ctx.iosPlacement.maxHeight}px`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            } : null,
          ]}
        >
          <ListSubheader sx={{ position: 'inherit' }}>
            <TextField
              placeholder={ctx.customSearchPlaceholder || `${ctx.t?.['search for']} ${ctx.splitName}`}
              value={ctx.search}
              onChange={(e) => {
                ctx.setSearch(e.target.value);
                ctx.handleInputChange(e.target.value);
              }}
              key="search-in-db"
              // Never autofocus here: this Paper only renders on iOS (see PaperComponent
              // below), and autofocusing this search field opens the keyboard on a plain
              // arrow tap - the user hasn't asked to type yet.
              variant="outlined"
              fullWidth
              margin="dense"
              name={`${ctx.splitName}`}
            />
          </ListSubheader>
          {children}
        </Paper>
      </ClickAwayListener>
    )
  }, [anchorRef])

  // Android and web keep exactly the previous rendering: an inline component,
  // recreated each render. The remount it causes is harmless there - only iOS
  // lost focus because of it.
  const InlinePaper = ({ children }) => (
  <ClickAwayListener onClickAway={onClickAway}>

    <Paper
      key={'Paper2'}
      anchorEl={anchorRef?.current}
      sx={[

        iosPlacement.maxHeight ? {
          maxHeight: `${iosPlacement.maxHeight}px`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        } : null,
      ]}
    >
      <ListSubheader sx={{ position: 'inherit' }}>
        <TextField
          placeholder={customSearchPlaceholder || `${t?.['search for']} ${splitName}`}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            handleInputChange(e.target.value);
          }}
          key={'search-in-db'}
          // Never autofocus here: autofocusing this search field opens the
          // keyboard on a plain arrow tap - the user hasn't asked to type yet.
          variant="outlined"
          fullWidth
          margin="dense"
          name={`${splitName}`}
        />
      </ListSubheader>
      {children}
    </Paper>

  </ClickAwayListener>
  )

  return (
    <FormControl sx={styles.formControlGrow} style={customStyle}
      ref={anchorRef}
    >
      <InputLabel
        htmlFor={name}
        sx={styles.formInputLabel}
      >
        {splitName}
        {required && <TeamSnap style={styles.snapIcon} data-testid="teamsnap-icon" />}
      </InputLabel>
      <Autocomplete
        id={name}
        limitTags={1}
        selectOnFocus={false}
        defaultValue={isNotEmpty(defaultOption) ? defaultOption : emptyValue}
        value={isNotEmpty(defaultOption) ? defaultOption : emptyValue}
        onKeyDown={(e) => { e.preventDefault(); }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        sx={styles.multiAutoComplete}
        options={options || []}
        getOptionLabel={(option) => option.name || option.english_name || ''}
        popupIcon={<KeyboardArrowDownIcon />}
        onChange={(_, newValue) => sendSelectedItem(newValue)}
        getOptionDisabled={() => false}
        multiple={multiple}
        clearOnBlur={!iosPlacement.keepTypedText}
        loading={reduxStore?.loading}
        loadingText={t?.Loading}
        noOptionsText={`${t?.NOT} ${splitName} ${t?.FOUND}`}
        ListboxProps={{
          sx: iosPlacement.maxHeight
            ? {
              maxHeight: 'unset', flex: 1, minHeight: 0, overflowY: 'auto',
            }
            : { maxHeight: '300px' },
        }}
        disableCloseOnSelect={true}
        renderOption={(props2, option, { selected, index }) => (
          <>
          <Box component="li" {...props2} sx={[{ mt: index === 0 ? '5px' : '0px' }, styles.option]}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              sx={styles.checkBoxStyles}
              checked={selected}
            />
            {nameConverter
              ? nameConverter(option)
              : option.name || option.english_name}
          </Box>
          {index === (options?.length || 0) - 1 && options?.length &&

          <LoadMoreBtn
            store={reduxStore}
            actionToDispatch={loadMoreActionToDispatch}
            actionQueries={actionQueries}
            customStyles={{ mb: '0px' }}
          />
          }
          </>

        )}
        PaperComponent={isIosDevice ? PopupPaper : InlinePaper}
        renderInput={(params) => (
          <TextField
            placeholder={`${t?.Select} ${customPlaceHolder || splitName}`}
            variant="outlined"
            fullWidth
            margin="dense"
            disabled
            {...params}
            slotProps={{
              input: {
                ...params.InputProps,
                onFocus: (e) => {
                  params.inputProps?.onFocus?.(e)
                  requestAnimationFrame(() => {
                    const input = e.target
                    if (
                      input?.setSelectionRange &&
                      document.activeElement === input
                    ) {
                      const len = input.value.length
                      input.setSelectionRange(len, len)
                    }
                  })
                },
                endAdornment: (
                  <>
                    {(reduxStore?.loading) ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        {...props}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        slotProps={{
          // Upwards on iOS, clear of the keyboard; capped by the hook.
          popper: iosPlacement.upwards ? iosPlacement.popperProps : undefined,
          popupIndicator: {
            onMouseDown: (e) => {
              e.preventDefault()
            },
            onClick: (e) => {
              e.stopPropagation()
              if (open) {
                handleClose(e, 'toggleInput')
              } else {
                handleOpen()
              }
            },
          },
        }}
      />
    </FormControl>
  );
};

const styles = {
  formControlGrow: {
    width: '100%',
    marginBottom: { xs: '0px', sm: '20px' },
    '& .MuiInputLabel-outlined.MuiInputLabel-marginDense': {
      transform: 'translate(14px,12px) scale(1)',
    },
    '& .MuiFormControl-marginDense': {
      marginTop: { xs: '5px', sm: '30px' },
      marginBottom: '0',
      paddingRight: '0',
    },
    '& .MuiOutlinedInput-root': {
      marginTop: { xs: '10px', sm: 'unset' },
      borderRadius: 4,
      backgroundColor: '#fafbfc',
      height: { xs: '40px', sm: 'unset' },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#29cc8b',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'solid 2px #e2e8ee',
      transition: 'all 0.3s ease-in-out',
    },
    '& .MuiOutlinedInput-inputMarginDense': {
      padding: { xs: '8px', sm: '12px' },
    },
    '& .MuiAlert-root': {
      width: '100%',
      marginBottom: { xs: 12, sm: 16 },
    },
    '& .MuiOutlinedInput-input': {
      fontSize: { xs: '12px', sm: '14px' },
    },
  },

  formInputLabel: {
    display: 'flex',
    alignItems: 'center',
    transform: 'translate(0,0) scale(1)',
    textTransform: 'capitalize',
    color: 'common.black',
    fontSize: { xs: '11px', md: '16px' },
    width: 'max-content',
    marginBottom: { xs: '10px', sm: '5px' },
    paddingRight: { xs: '14px', sm: '18px' },
    '& svg': {
      mx: '10px',
    },
  },
  multiAutoComplete: {
    '& .MuiChip-label': {
      fontSize: { xs: '10px', sm: '0.875rem' },
      paddingLeft: { xs: '4px', sm: '8px' },
      paddingRight: { xs: '4px', sm: '8px' },
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      height: { xs: '21px', sm: '25px' },
      color: '#000',
    },

    '& .MuiChip-deleteIcon': {
      color: '#29cc8b',
      fill: '#29cc8b',
      width: '18px',
      margin: '0px 6px 0px -2px',
      cursor: 'pointer',
    },

    '& .MuiSvgIcon-root': {
      fill: 'currentColor',
      display: 'inline-block',
      fontSize: { xs: '1rem', sm: '1.5rem' },
      transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      flexShrink: 0,
      userSelect: 'none',
    },

    '& .MuiAutocomplete-tag': {
      paddingRight: '5px',
      paddingBottom: '3px',
      borderRadius: '10px',
      border: 'solid 1px rgba(41, 204, 139, 0.59)',
      backgroundColor: '#edfff8',
      '& .MuiChip-deleteIcon': { fill: '#29cc8b' },
    },
    '& input': {
      fontSize: '14px',
      height: {
        xs: '4px',
        sm: '20px',
      },
    },
  },
  formInput: {
    height: { xs: '40px', sm: 'unset' },
  },
  option: {
    fontSize: { xs: '14px', sm: '16px' },
    fontFamily: 'Avenir',
  },
  checkBoxStyles: {
    fontSize: '14px !important',
  },
}

export default OfoqMultiAutoCompleteWithLoadMore
